<template>
  <AuthenticatedLayout activeMenuItem='home' title='Home'>
    <PaddedLayout>
      <div class='flex flex-wrap py-2 justify-between items-center gap-2 w-full'>
        <h1 class='text-2xl'>Welcome, {{ getFullName() }}</h1>
      </div>
      <LoadingLayout :isLoading='isLoading'>
        <div class='mt-6 max-w-[80rem]'>
          <div class='flex flex-col md:flex-row gap-8'>
            <div class='md:w-1/2'>
              <dl class="flex flex-col gap-5">
                <template v-if='shouldShowOrganisation()'>
                  <ContextualRouterLink :to='mostRecentAssessment.url' class="overflow-hidden rounded-lg bg-gray-50 px-4 py-5 shadow sm:p-6" v-if='mostRecentAssessment'>
                    <dt class="truncate text-sm font-medium text-gray-500">Resume Assessment</dt>
                    <dd class="mt-1 text-3xl font-semibold tracking-tight text-gray-900">{{ mostRecentAssessment.year }}: {{ mostRecentAssessment.name }}</dd>
                  </ContextualRouterLink>
                  <ContextualRouterLink :to='createAssessmentUrl' class="overflow-hidden rounded-lg bg-gray-50 px-4 py-5 shadow sm:p-6">
                    <dt class="truncate text-sm font-medium text-gray-500">Create Assessment</dt>
                    <dd class="mt-1 text-3xl font-semibold tracking-tight text-gray-900">Start new assessment</dd>
                  </ContextualRouterLink>
                </template>
                <div class="overflow-hidden rounded-lg bg-gray-50 px-4 py-5 shadow sm:p-6 flex-1">
                  <dt class="truncate text-sm font-medium text-gray-500">{{ themeGraphParams.year.from }} Portfolio Engagement Score Overview</dt>
                  <GraphAnnualThemePie :params='themeGraphParams' :condensed='true' />
                </div>
                <template v-if='shouldShowSuperAdmin()'>
                  <ContextualRouterLink to='/report/annual-portfolio' class="overflow-hidden rounded-lg bg-gray-50 px-4 py-5 shadow sm:p-6">
                    <dt class="truncate text-sm font-medium text-gray-500">Reports</dt>
                    <dd class="mt-1 text-3xl font-semibold tracking-tight text-gray-900">View annual portfolio report</dd>
                  </ContextualRouterLink>
                </template>
              </dl>
            </div>
            <div class='md:w-1/2 flex flex-col gap-5'>
              <div class='overflow-hidden rounded-lg bg-gray-50 px-4 py-5 shadow sm:p-6'>
                <h2 class='text-xl'>Announcements</h2>
                <ul class='divide-y' v-if='announcements?.length'>
                  <li v-for='announcement in announcements' class='py-3 text-sm'>
                    <div class='flex justify-between gap-2'>
                      <h2 class='font-medium'>{{ announcement.headline }}</h2>
                      <span class='text-gray-400'>{{ (new Date(announcement.date)).toLocaleDateString() }}</span>
                    </div>
                    <p class='mt-2' v-if='announcement.excerpt'>{{ announcement.excerpt }}</p>
                    <p class='mt-2' v-if='announcement.link'>
                      <a target='_blank' rel='noopener noreferrer' :href='announcement.link' class='text-jananavy-900 font-medium inline-flex gap-2 items-center'>
                        Read more
                        <ArrowTopRightOnSquareIcon class='w-4 h-4 text-jananavy-900' aria-hidden='true' />
                      </a>
                    </p>
                  </li>
                </ul>
                <div class='p-3 border border-dashed rounded-md mt-3 text-sm' v-else>
                  No current announcements
                </div>
              </div>
              <div class='overflow-hidden rounded-lg bg-gray-50 px-4 py-5 shadow sm:p-6'>
                <h2 class='text-xl'>Guide</h2>
                <p class='mt-2'>To get the best out of this platform follow this recommended workflow.</p>
                <ol class='mt-2'>
                  <li class='flex gap-2 mt-4' v-for='tip, index in tips'>
                    <div>
                      <span class='bg-janaturq-400 text-white rounded-md w-11 h-11 inline-flex items-center justify-center font-bold'>{{ index + 1 }}</span>
                    </div>
                    <div>
                      <h3 class='text-lg'>{{ tip.title }}</h3>
                      <p class='text-sm'>{{ tip.text }}</p>
                    </div>
                  </li>
                </ol>
              </div>
            </div>
          </div>
        </div>
      </LoadingLayout>
    </PaddedLayout>
  </AuthenticatedLayout>
</template>

<script setup>

import { computed, watch } from 'vue';

import ContextualRouterLink from '@/components/links/contextual-router-link.vue';

import {
  ArrowTopRightOnSquareIcon
} from '@heroicons/vue/24/outline';

import AuthenticatedLayout from '@/components/layouts/authenticated-layout.vue';
import PaddedLayout from '@/components/layouts/padded-layout.vue';
import LoadingLayout from '@/components/layouts/loading-layout.vue';

import GraphAnnualThemePie from '@/components/reports/graphs/graph-annual-theme-pie.vue';

import { getFullName, shouldShowSuperAdmin, shouldShowOrganisation, getOrganisationId } from '@/kernel/session';
import { usePayloadRequest } from '@/kernel/api';
import { getCollection } from '@/kernel/collections';

const assessmentCollection = getCollection('assessment');
const createAssessmentUrl = assessmentCollection.getCreateUrl();

const themeGraphParams = {
  organisation: shouldShowOrganisation() ? getOrganisationId() : null,
  dimension: 'theme',
  resolution: 'year',
  year: {
    from: (new Date()).getFullYear(),
    to: (new Date()).getFullYear()
  }
}

const mostRecentAssessmentRequest = usePayloadRequest('GET', '/assessment');
const announcementsRequest = usePayloadRequest('GET', '/announcement');

const isLoading = computed(() => {
  return mostRecentAssessmentRequest.isLoading() && announcementsRequest.isLoading();
});

const mostRecentAssessment = computed(() => {
  const data = mostRecentAssessmentRequest.getData();

  const assessment = data?.docs?.[0];

  if (!assessment) {
    return null;
  }

  let year = assessment.year;

  if (assessment.halfYear) {
    year += ` H${assessment.halfYear}`;
  }

  let name = assessment.organisationAsset.name;

  if (assessment.organisationAssetManager) {
    name += ` / ${assessment.organisationAssetManager.name}`;
  }

  const url = assessmentCollection.getInterviewUrl(assessment);

  return {
    year,
    name,
    url
  }
});

const announcements = computed(() => {
  const data = announcementsRequest.getData();

  if (!data?.docs?.length) {
    return null;
  }

  return data.docs;
});

const load = () => {

  const where = {
    status: {
      not_equals: 'complete'
    }
  }

  const organisationId = getOrganisationId();

  if (organisationId) {
    where.organisation = {equals: organisationId}
  }

  mostRecentAssessmentRequest.doRequest({
    params: {
      where,
      sort: '-updatedAt',
      limit: 1,
      depth: 1
    }
  });

  announcementsRequest.doRequest({
    params: {
      sort: '-date',
      where: {
        isPublished: {equals: true}
      }
    }
  });

}

watch(() => getOrganisationId(), () => {
  load();
});

load();

const tips = [
  {title: 'Setup assets', text: 'Enter the client assets your organisation is interested in evaluating.'},
  {title: 'Setup asset managers', text: 'Enter the asset managers your organisation has engaged for those assets.'},
  {title: 'Browse the content library', text: 'Familiarise yourself with the themes and objectives used to evaluate an asset.'},
  {title: 'Create an assessment template', text: 'Choose which objectives your organisation wishes to evaluate for one or more assets as a group.'},
  {title: 'Complete the assessments', text: 'Follow the objectives in a template to evaluate each asset and its manager by entering a score and evidentiary notes.'},
  {title: 'Generate a report', text: 'See the outcomes of your assessments and gain guidance for improvement.'},
];

</script>
